import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from 'src/app/core/store';
import { ReimbursementService } from 'src/app/core/services/reimbursement.service';
import { AppPermission } from 'src/app/models/appEnums';
import { ReimbursementTypeModel } from 'src/app/models/reimbursementModel';
import { AlertService } from 'src/app/shared/services/alert.service';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';

@Component({
  selector: 'app-reimbursement-type-management',
  templateUrl: './reimbursement-type-management.component.html',
  standalone: false
})
export class ReimbursementTypeManagementComponent extends BaseComponent implements OnInit {
  types: ReimbursementTypeModel[] = [];
  canEdit = false;
  loading = true;
  savingID: number = null;
  newType: ReimbursementTypeModel = this.emptyType();

  constructor(
    private reimbursementService: ReimbursementService,
    private alertService: AlertService,
    store: Store<State>) {
    super();
    this.subs.push(store.select(state => state.accountState.user?.permissionIDs).subscribe(permissionIDs => {
      this.canEdit = (permissionIDs || []).includes(AppPermission.HRPortalReimbursementTypesEdit);
    }));
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.reimbursementService.getAllTypes().subscribe({
      next: types => {
        this.types = types;
        this.loading = false;
        this.newType = this.emptyType();
      },
      error: error => {
        this.loading = false;
        this.alertService.error(this.errorMessage(error, 'Unable to load reimbursement types.'));
      }
    });
  }

  add(): void {
    if (!this.canEdit || !this.valid(this.newType)) {
      return;
    }
    this.savingID = 0;
    const submitted = { ...this.newType };
    this.reimbursementService.addType(submitted).subscribe({
      next: saved => {
        this.types = [...this.types, saved].sort((a, b) =>
          a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
        this.newType = this.emptyType();
        this.savingID = null;
        this.alertService.success('Reimbursement type added.');
      },
      error: error => {
        this.savingID = null;
        this.alertService.error(this.errorMessage(error, 'Unable to add the reimbursement type.'));
      }
    });
  }

  save(type: ReimbursementTypeModel): void {
    if (!this.canEdit || !this.valid(type)) {
      return;
    }
    this.savingID = type.id;
    const submitted = { ...type };
    this.reimbursementService.updateType(submitted).subscribe({
      next: saved => {
        const index = this.types.findIndex(item => item.id === saved.id);
        this.types.splice(index, 1, saved);
        this.types = [...this.types].sort((a, b) =>
          a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
        this.savingID = null;
        this.alertService.success('Reimbursement type saved.');
      },
      error: error => {
        this.savingID = null;
        this.alertService.error(this.errorMessage(error, 'Unable to save the reimbursement type.'));
        this.load();
      }
    });
  }

  valid(type: ReimbursementTypeModel): boolean {
    return !!type.name?.trim() && !!type.payrollExportCode?.trim()
      && type.displayOrder != null && type.displayOrder >= 0;
  }

  private emptyType(): ReimbursementTypeModel {
    return {
      id: 0,
      name: '',
      payrollExportCode: '',
      isActive: true,
      displayOrder: this.types.length ? Math.max(...this.types.map(type => type.displayOrder)) + 10 : 10,
      allowAAInitialApproval: false
    };
  }

  private errorMessage(error: any, fallback: string): string {
    return typeof error?.error === 'string' ? error.error : fallback;
  }
}
